import { useEffect, useRef, useState } from 'react'
import { GlobalWorkerOptions, getDocument, RenderTask } from 'pdfjs-dist'
// Vite will serve the worker from this URL
// eslint-disable-next-line import/no-unresolved
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import PreviewLoader from './PreviewLoader'
import css from './preview.module.css'
import ThreeDotsLoader from '@/shared/Loaders/ThreeDotsLoader'

GlobalWorkerOptions.workerSrc = workerSrc

interface IProps {
  url: string
}

const PdfPreview = ({ url }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const renderTaskRef = useRef<RenderTask | null>(null)
  const pdfRef = useRef<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pages, setPages] = useState<number[]>([])
  const [containerWidth, setContainerWidth] = useState<number>(0)

  const canvasRefs = useRef<Record<number, HTMLCanvasElement | null>>({})

  const setCanvasRef = (pageNumber: number) => (el: HTMLCanvasElement | null) => {
    canvasRefs.current[pageNumber] = el
  }

  const isCancelError = (err: any) => {
    const name = err?.name || ''
    const message = (err?.message || '').toLowerCase()
    return (
      name === 'RenderingCancelledException' ||
      name === 'AbortException' ||
      name === 'AbortError' ||
      message.includes('aborted') ||
      message.includes('cancelled')
    )
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    setPages([])

    const task = getDocument({ url, withCredentials: true })
    task.promise
      .then((pdf) => {
        if (cancelled) return
        pdfRef.current = pdf
        const totalPages = pdf.numPages
        setPages(Array.from({ length: totalPages }, (_, idx) => idx + 1))
      })
      .catch((err) => {
        if (!cancelled && !isCancelError(err)) {
          setError(true)
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
      renderTaskRef.current?.cancel()
      task.destroy()
      pdfRef.current = null
    }
  }, [url])

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth)
      }
    })
    observer.observe(containerRef.current)
    setContainerWidth(containerRef.current.clientWidth)

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!pdfRef.current || pages.length === 0) return
    let cancelled = false
    const renderPages = async () => {
      try {
        for (const pageNumber of pages) {
          if (cancelled) break
          const page = await pdfRef.current.getPage(pageNumber)
          if (cancelled) break
          const canvas = canvasRefs.current[pageNumber]
          if (!canvas) continue

          const viewport = page.getViewport({ scale: 1 })
          const targetWidth = (canvas.parentElement?.clientWidth || containerWidth || viewport.width) ?? viewport.width
          const scale = Math.min(targetWidth / viewport.width, 2)
          const scaledViewport = page.getViewport({ scale })

          const context = canvas.getContext('2d')
          if (!context) continue

          canvas.height = scaledViewport.height
          canvas.width = scaledViewport.width

          const renderTask = page.render({ canvasContext: context, viewport: scaledViewport })
          renderTaskRef.current = renderTask
          await renderTask.promise
        }
        if (!cancelled) setLoading(false)
      } catch (e) {
        if (!cancelled && !isCancelError(e)) {
          setError(true)
          setLoading(false)
        }
      }
    }

    renderPages()

    return () => {
      cancelled = true
      renderTaskRef.current?.cancel()
    }
  }, [pages, containerWidth])

  if (error) {
    return <div className={css.pdfError}>Не удалось загрузить PDF</div>
  }

  return (
    <div className={css.pdfViewer} ref={containerRef}>
      {loading && !pages.length && (
        <PreviewLoader>
          <ThreeDotsLoader inscription="Загружаем файл..." />
        </PreviewLoader>
      )}
      <div className={css.pdfPages}>
        {pages.map((pageNumber) => (
          <canvas key={pageNumber} ref={setCanvasRef(pageNumber)} className={css.pdfCanvas} />
        ))}
      </div>
    </div>
  )
}

export default PdfPreview
